import React, { createContext, useContext, useEffect } from 'react';
import { ThemeConfig } from 'simple-creator/types';

interface ThemeContextValue {
  theme: Required<ThemeConfig>;
}

const defaultTheme: Required<ThemeConfig> = {
  primaryColor: '#BF2088',
  fontFamily: 'Syne, sans-serif',
  borderRadius: 'md',
  darkMode: false,
};

const ThemeContext = createContext<ThemeContextValue>({
  theme: defaultTheme,
});

interface ThemeProviderProps {
  children: React.ReactNode;
  theme?: ThemeConfig;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  theme = {},
}) => {
  const mergedTheme = { ...defaultTheme, ...theme };
  
  // Apply theme CSS variables
  useEffect(() => {
    const root = document.documentElement;
    
    // Set primary color
    if (mergedTheme.primaryColor) {
      root.style.setProperty('--primary-color', mergedTheme.primaryColor);
    }
    
    // Set font family
    if (mergedTheme.fontFamily) {
      root.style.setProperty('--font-family', mergedTheme.fontFamily);
    }
    
    // Apply dark mode class
    if (mergedTheme.darkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
    
    // Cleanup
    return () => {
      document.body.classList.remove('dark-mode');
    };
  }, [mergedTheme]);
  
  return (
    <ThemeContext.Provider value={{ theme: mergedTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  return context;
};
